import React from "react";
import Hero from "./Hero";
import Reveal from "./Reveal";
import Rating from "./Rating";
import ProjectRow from "./ProjectRow";
import SubscriptionForm from "./SubscriptionForm";
import { LoadingNotice, LoadError, isPending } from "./LoadState";
import { LINKEDIN_URL } from "./Footer";
import { ArrowUpRight } from "lucide-react";

// Gallery mosaic: one cell per photo, never an empty cell. Counts that can't
// fill a clean layout (4, 6+) are trimmed to the nearest one that can.
const MOSAIC = {
  1: { count: 1, grid: "grid-cols-1", first: "aspect-[16/9]" },
  2: { count: 2, grid: "grid-cols-2", first: "aspect-[4/3]" },
  3: {
    count: 3,
    grid: "grid-cols-2 md:h-[28rem] md:grid-cols-3 md:grid-rows-2",
    first: "col-span-2 aspect-[16/10] md:row-span-2 md:aspect-auto",
  },
  5: {
    count: 5,
    grid: "grid-cols-2 md:h-[30rem] md:grid-cols-4 md:grid-rows-2",
    first: "col-span-2 aspect-[16/10] md:row-span-2 md:aspect-auto",
  },
};
const mosaicFor = (n) => MOSAIC[n >= 5 ? 5 : n === 4 ? 3 : n];

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const Section = ({ title, action, children, className = "" }) => (
  <Reveal as="section" className={`py-14 md:py-20 ${className}`}>
    <div className="flex flex-wrap items-center justify-between gap-4">
      <h2 className="section-title">{title}</h2>
      {action}
    </div>
    <div className="mt-8 md:mt-10">{children}</div>
  </Reveal>
);

const HomePage = ({
  reviews,
  subscribers,
  setCurrentPage,
  onAddSubscriber,
  projects,
  images,
  status,
  onRetry,
  isAuthenticated,
}) => {
  // Visitors never see an empty section; the admin sees it with a hint to fill it.
  const showProjects = status.projects !== "ready" || projects.length > 0 || isAuthenticated;
  const showGallery = status.images !== "ready" || images.length > 0 || isAuthenticated;
  const showReview = status.reviews !== "ready" || reviews.length > 0 || isAuthenticated;

  const mosaic = images.length > 0 ? mosaicFor(images.length) : null;
  const latest = reviews[0];

  return (
    <>
      <Hero setCurrentPage={setCurrentPage} />

      <div className="page">
        {/* About: a single reading column */}
        <Reveal as="section" className="py-14 md:py-20">
          <h2 className="section-title">About Me</h2>
          <p className="mt-6 max-w-[65ch] text-lg leading-relaxed text-slate-300 md:text-xl">
            Hi! My name is Nabil! Just a coder and traveler who enjoys watching
            sports and movies.
          </p>
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-link mt-6">
            Find me on LinkedIn
            <ArrowUpRight size={16} strokeWidth={1.75} />
          </a>
          {isAuthenticated && (
            <p className="mt-6 font-mono text-sm text-slate-400">
              Admin: {subscribers.length} {subscribers.length === 1 ? "subscriber" : "subscribers"}
            </p>
          )}
        </Reveal>

        {/* Featured projects: editorial rows */}
        {showProjects && (
          <Section title="Featured Projects">
            {isPending(status.projects) && (
              <>
                <div className="divide-y divide-white/5" aria-hidden="true">
                  {[0, 1].map((i) => (
                    <div key={i} className="grid gap-4 py-8 md:grid-cols-12 md:gap-8">
                      <div className="h-7 w-2/3 rounded-full bg-slate-900 motion-safe:animate-pulse md:col-span-5" />
                      <div className="space-y-3 md:col-span-7">
                        <div className="h-4 rounded-full bg-slate-900 motion-safe:animate-pulse" />
                        <div className="h-4 w-4/5 rounded-full bg-slate-900 motion-safe:animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
                <LoadingNotice label="projects" slow={status.projects === "slow"} onRetry={onRetry.projects} />
              </>
            )}
            {status.projects === "error" && <LoadError label="projects" onRetry={onRetry.projects} />}
            {status.projects === "ready" && projects.length === 0 && (
              <p className="text-slate-400">No projects yet. Add one from the Projects page and it will be featured here.</p>
            )}
            {status.projects === "ready" && projects.length > 0 && (
              <div className="divide-y divide-white/5 border-t border-white/5">
                {projects.slice(0, 3).map((project) => (
                  <ProjectRow key={project.id} project={project} />
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Gallery: asymmetric mosaic, exact cell count */}
        {showGallery && (
          <Section
            title="Photo Gallery"
            action={
              status.images === "ready" && images.length > 0 ? (
                <button type="button" onClick={() => setCurrentPage("gallery")} className="btn-secondary">
                  View gallery
                </button>
              ) : null
            }
          >
            {isPending(status.images) && (
              <>
                <div className="grid grid-cols-2 gap-3 md:h-[28rem] md:grid-cols-3 md:grid-rows-2" aria-hidden="true">
                  <div className="col-span-2 aspect-[16/10] rounded-xl bg-slate-900 motion-safe:animate-pulse md:row-span-2 md:aspect-auto" />
                  <div className="aspect-square rounded-xl bg-slate-900 motion-safe:animate-pulse md:aspect-auto" />
                  <div className="aspect-square rounded-xl bg-slate-900 motion-safe:animate-pulse md:aspect-auto" />
                </div>
                <LoadingNotice label="photos" slow={status.images === "slow"} onRetry={onRetry.images} />
              </>
            )}
            {status.images === "error" && <LoadError label="photos" onRetry={onRetry.images} />}
            {status.images === "ready" && images.length === 0 && (
              <p className="text-slate-400">No photos yet. Upload some from the Gallery page and they will appear here.</p>
            )}
            {status.images === "ready" && mosaic && (
              <div className={`grid gap-3 ${mosaic.grid}`}>
                {images.slice(0, mosaic.count).map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => setCurrentPage("gallery")}
                    aria-label={`Open the gallery: ${photo.title || "travel photo"}`}
                    className={`overflow-hidden rounded-xl bg-slate-900 ${
                      index === 0 ? mosaic.first : "aspect-square md:aspect-auto"
                    }`}
                  >
                    <img
                      src={photo.thumbUrl || photo.url}
                      alt={photo.title || (photo.category ? `${photo.category} photo` : "Travel photo")}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Latest review: a pull quote, right before the newsletter it feeds */}
        {showReview && (
          <Section title="Latest review">
            {isPending(status.reviews) && (
              <LoadingNotice label="reviews" slow={status.reviews === "slow"} onRetry={onRetry.reviews} />
            )}
            {status.reviews === "error" && <LoadError label="reviews" onRetry={onRetry.reviews} />}
            {status.reviews === "ready" && !latest && (
              <p className="text-slate-400">No reviews yet. Publish one from the Reviews page and it will appear here.</p>
            )}
            {status.reviews === "ready" && latest && (
              <figure className="grid gap-6 md:grid-cols-12 md:gap-8">
                <figcaption className="space-y-3 md:col-span-4">
                  <p className="break-words text-xl font-semibold tracking-tight text-slate-50">{latest.title}</p>
                  <Rating value={latest.rating} />
                  {formatDate(latest.date) && (
                    <time dateTime={latest.date} className="block font-mono text-xs text-slate-400">
                      {formatDate(latest.date)}
                    </time>
                  )}
                </figcaption>
                <blockquote className="md:col-span-8">
                  <p className="line-clamp-3 max-w-[60ch] text-xl leading-snug text-slate-100 md:text-2xl">
                    “{latest.excerpt}”
                  </p>
                </blockquote>
              </figure>
            )}
          </Section>
        )}

        {/* Newsletter: the one raised surface on the page */}
        <Reveal as="section" className="pb-20 pt-8 md:pb-28">
          <div className="grid gap-8 rounded-xl bg-slate-900 p-6 ring-1 ring-white/10 sm:p-10 md:grid-cols-12 md:items-center md:gap-12 md:p-12">
            <div className="md:col-span-5">
              <h2 className="section-title">Stay Updated</h2>
              <p className="mt-4 text-slate-300">
                Subscribe to get notified when new reviews are published.
              </p>
            </div>
            <div className="md:col-span-7">
              <SubscriptionForm onAddSubscriber={onAddSubscriber} />
            </div>
          </div>
        </Reveal>
      </div>
    </>
  );
};

export default HomePage;
